import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactStars from "react-rating-stars-component";
import patientAvatar from '../reviews/avatar.png'
import { getToken } from "auth/authenticate";
import { ApiEndpoint } from "config/constants/apiConstants";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  background: "#fff",
  boxShadow: "0 4px 24px 0 rgba(33, 150, 243, 0.10)",
  padding: theme.spacing(3, 2),
  maxWidth: 400,
  margin: "0 auto",
  border: "1px solid #e3f2fd",
}));

const QuickReviewBox = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  textAlign: "center",
  background: "#e3f2fd",
  borderRadius: 8,
  padding: theme.spacing(1, 2),
  margin: theme.spacing(0.5),
  transition: "background 0.2s",
  "&:hover": {
    background: "#bbdefb",
  },
}));

const ReviewCard = ({ appointment, onReviewSent }) => {
  const [review, setReview] = useState("");
  const [rating, setRating] = useState(3);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const quickReviews = [
    { text: "Not Bad", emoji: "🙂" },
    { text: "Love It", emoji: "😊" },
    { text: "Nice Work", emoji: "😁" },
    { text: "Awesome", emoji: "🤩" },
  ];

  const handleSend = async () => {
    if (!review || review.length < 2) {
      setError("Please type something");
      return;
    }
    setError("");
    setSending(true);
    try {
      const token = getToken();
      await fetch(ApiEndpoint.WRITE_REVIEW, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          rate: rating,
          comment: review,
          appointmentId: appointment?.appointmentId,
          toWhom: appointment?.Doctor?.doctorId,
        }),
      });
      setReview("");
      setRating(3);
      setSending(false);
      if (onReviewSent) onReviewSent();
    } catch (err) {
      setError("Failed to send review");
      setSending(false);
    }
  };

  return (
    <StyledPaper elevation={0}>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Avatar
          alt="Doctor Avatar"
          src={patientAvatar}
          variant="rounded"
          sx={{
            margin: "0 auto",
            width: 56,
            height: 56,
            border: "2px solid #1976d2",
            boxShadow: "0 2px 8px #e3f2fd",
          }}
        />
        <Typography
          gutterBottom
          marginTop={1}
          fontSize={15}
          sx={{ color: "#1976d2", fontWeight: 600 }}
        >
          {appointment?.Doctor?.name ?? "Doctor"}
        </Typography>
      </Box>

      <Box sx={{ textAlign: "center", mb: 2 }}>
        <ReactStars
          count={5}
          size={32}
          value={rating}
          onChange={setRating}
          activeColor="#1976d2"
          color="#90caf9"
        />
      </Box>

      <TextField
        multiline
        minRows={3}
        fullWidth
        margin="normal"
        value={review}
        onChange={(e) => setReview(e.target.value)}
        placeholder="Write your review here..."
        sx={{
          background: "#f5faff",
          borderRadius: 2,
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#90caf9",
            },
            "&:hover fieldset": {
              borderColor: "#1976d2",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1976d2",
            },
          },
        }}
      />

      {error && (
        <Typography color="error" align="center" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      <Grid
        container
        spacing={1}
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 1, mb: 2 }}
      >
        {quickReviews.map(({ text, emoji }) => (
          <Grid item key={text}>
            <QuickReviewBox onClick={() => setReview(text)}>
              <Box sx={{ fontSize: "22px" }}>{emoji}</Box>
              <Typography fontSize={12} sx={{ color: "#1976d2", fontWeight: 500 }}>
                {text}
              </Typography>
            </QuickReviewBox>
          </Grid>
        ))}
      </Grid>

      <Button
        fullWidth
        variant="contained"
        onClick={handleSend}
        disabled={sending}
        sx={{
          mt: 1,
          background: "#1976d2",
          color: "#fff",
          fontWeight: 600,
          borderRadius: 2,
          boxShadow: "0 2px 8px #e3f2fd",
          "&:hover": {
            background: "#1565c0",
          },
        }}
      >
        {sending ? "Sending..." : "Send Review"}
      </Button>
    </StyledPaper>
  );
};

export default ReviewCard;